// @ts-strict-ignore
import React, { useCallback, useContext, useState } from 'react';
import {
  Button, ButtonSize, ButtonVariant, IconName, Text, useSnackBar,
} from '@usealloy/component-library';
import { useFormContext } from 'react-hook-form';
import type { FormState } from '../types';
import type { NodeEditorDrawerFooterProps } from '../../useNodeEditorForm';
import { tagGlobalSettingsFormAdapter, getChangedTagGlobalSettingsFieldElementIds } from '../helpers';
import { selectFocusedAction, selectWorkflowProps, selectTagByName } from '~/dashboard/views/workflows/store/redux/selectors';
import { StyledBadge, StyledFooter, StyledSpacer } from './styles';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import apiCalls from '~/helpers/apiCalls';
import { updateTagInProps, updateWorkflowProps } from '~/dashboard/views/workflows/store/redux/slice';
import { getOutcomeByName } from '~/dashboard/views/workflows/service/outcomes/outcomes';
import { UserContext } from '~/dashboard/context';
import type { TagObjectInAction } from 'flow/store/engine/types';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';


export default function Footer({
  onCancel,
  onSave,
  saveDisabled = true,
  setIsFormDisabled,
}: NodeEditorDrawerFooterProps<FormState>) {
  const addSnackBar = useSnackBar();
  const dispatch = useAppDispatch();
  const formMethods = useFormContext<FormState>();
  const focusedAction = useAppSelector(selectFocusedAction);
  // comparing to focusedAction tag name instead of editedTag tag name
  // since the edited name could be different and matchingTagObject will be null
  const matchingTagObject = useAppSelector(selectTagByName(focusedAction.tag.name));
  const workflowProps = useAppSelector(selectWorkflowProps);
  const { outcomes } = workflowProps;
  const { featureFlags } = useContext(UserContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const updateTagRemote = useCallback(async (
    editedTag: TagObjectInAction,
  ) => {
    const matchingOutcomes = editedTag.outcomes.map(
      selectedOutcome => outcomes.find(outcome => outcome.outcome === selectedOutcome.outcome),
    );
    // payload body `tag_outcomes` is a string value of the last added outcome customer ID
    // this property isn't needed in payload when there's only 1 outcome selected
    // but when there's multiple, we need the last selected outcome id
    let lastSelectedTagOutcomeId = '';
    if (editedTag.outcomes.length) {
      const lastSelectedOutcome = editedTag.outcomes.at(-1).outcome;
      const matchingOutcomeObject = getOutcomeByName(matchingOutcomes, lastSelectedOutcome);
      lastSelectedTagOutcomeId = matchingOutcomeObject.customer_outcome_id.toString();
    }

    try {
      const response = await apiCalls.settings.updateTag({
        tag: {
          app_version_count: matchingTagObject.app_version_count,
          archived: editedTag.archived,
          tag_name: editedTag.name,
          tag_color: editedTag.color,
          customer_tag_id: matchingTagObject.customer_tag_id,
          description: editedTag.description,
          category_id: editedTag.categoryId,
          category_name: editedTag.categoryName,
          tag_outcomes: lastSelectedTagOutcomeId,
          outcomes: matchingOutcomes,
          is_adverse_action_reason: editedTag.isAdverseActionReason,
          is_case_creating: editedTag.isCaseCreating,
          tag_type_name: editedTag.associatedTypes?.[0] || '',
          associated_types: editedTag.associatedTypes,
          suppression_count: editedTag.suppressionInterval?.value?.toString() || '',
          suppression_interval: editedTag.suppressionInterval,
        },
      });

      if (response.result === 'success') {
        setIsLoading(false);
        formMethods.handleSubmit(onSave)();
        dispatch(updateWorkflowProps({ tags: response.tags }));
      } else if (response.result === 'error') {
        setIsLoading(false);
        formMethods.setError(
          'name',
          { message: response.message, type: `${response.status}` },
        );
        setIsFormDisabled(false);
      }
    } catch (error) {
      setIsFormDisabled(false);
      setIsLoading(false);
      addSnackBar({
        title: `Could not update Tag ${editedTag.name}`,
        type: 'danger',
        duration: 2_000,
      });
    }
  }, [
    addSnackBar,
    dispatch,
    formMethods,
    onSave,
    setIsFormDisabled,
    outcomes,
    matchingTagObject?.customer_tag_id,
    matchingTagObject?.app_version_count,
  ]);

  const updateTagLocal = useCallback((
    updatedTagData: TagObjectInAction,
    formData: FormState,
  ) => {
    const sourceFormData = tagGlobalSettingsFormAdapter.getDefaultFormValues(
      focusedAction, workflowProps,
    );
    const [htmlElementId, ...relatedHtmlElementIds] = getChangedTagGlobalSettingsFieldElementIds(
      formData, sourceFormData,
    );

    const stateChange = () => dispatch(
      updateTagInProps({ updatedTagData, actionId: focusedAction.publicId }),
    );
    dispatch(createWorkflowHistoryThunk(
      stateChange,
      Boolean(featureFlags?.workflowEditorUndoRedo),
      {
        operation: Operation.Modify,
        uiEffectContext: createUiEffectContext(changeDescription.globalSettings, {
          actionId: focusedAction.publicId,
          ...(htmlElementId && { htmlElementId }),
          ...(relatedHtmlElementIds.length && { relatedHtmlElementIds }),
        }),
      },
    ));
    setIsLoading(false);
    onSave(formData);
  }, [
    dispatch, onSave, focusedAction, workflowProps, featureFlags?.workflowEditorUndoRedo,
  ]);

  // Wrapped handler that triggers validation before submit
  // handleSubmit will pass the validated form data as the first parameter
  const handleValidSubmit = useCallback(async (formData: FormState) => {
    setIsFormDisabled(true);
    setIsLoading(true);
    const editedTag = tagGlobalSettingsFormAdapter.mapFormDataToAction(
      formData, focusedAction,
    ).tag;
    if (featureFlags.useTyphonWorkflowEndpoints) {
      // this will need to handle both ephemeral and non-ephemeral tags
      updateTagLocal(editedTag, formData);
    } else {
      updateTagRemote(editedTag);
    }
  }, [
    featureFlags.useTyphonWorkflowEndpoints,
    focusedAction,
    setIsFormDisabled,
    updateTagRemote,
    updateTagLocal,
  ]);

  // Handler for validation errors
  const handleInvalidSubmit = useCallback(() => {
    setIsFormDisabled(false);
    setIsLoading(false);
  }, [setIsFormDisabled]);

  return (
    <StyledFooter gap={2}>
      <StyledBadge
        icon={IconName.Exclamation}
        outline
        status="attention"
        style={{
          whiteSpace: 'normal',
          height: 'auto',
          minHeight: 'var(--cl-space-5)',
        }}
      >
        <Text>
          {featureFlags.useTyphonWorkflowEndpoints
            ? 'Any changes to a tag\'s global settings will not take effect until the Workflow version is saved, and will impact all Workflows where the tag is used'
            : 'Any changes to a tag\'s global settings will impact all Workflows where the tag is used.'}
        </Text>
      </StyledBadge>
      <StyledSpacer />
      <Button
        data-dd-action-name="global-tag-settings-cancel"
        onClick={() => {
          if (focusedAction) {
            // Reset form to default values before closing (only on cancel, not on drawer close)
            const defaultValues = tagGlobalSettingsFormAdapter.getDefaultFormValues(
              focusedAction,
              workflowProps,
            );
            formMethods.reset(defaultValues);
          }
          formMethods.clearErrors();
          onCancel();
        }}
        size={ButtonSize.small}
        variant={ButtonVariant.secondary}
      >
        Cancel
      </Button>
      <Button
        data-dd-action-name="global-tag-settings-save"
        disabled={saveDisabled}
        loading={isLoading}
        onClick={formMethods.handleSubmit(handleValidSubmit, handleInvalidSubmit)}
        size={ButtonSize.small}
        variant={ButtonVariant.primary}
      > {featureFlags.useTyphonWorkflowEndpoints ? 'Apply' : 'Save'}
      </Button>
    </StyledFooter>

  );
}
